import React, { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import * as Sentry from '@sentry/react'
import Header from './components/Header'
import MaintenancePage from './pages/MaintenancePage'; // Import Maintenance Page
import { Analytics } from "@vercel/analytics/react"
import { SpeedInsights } from "@vercel/speed-insights/react"
import HeroSection from './components/HeroSection'

import MapSection from './components/MapSection'
// import ListingsSection from './components/ListingsSection'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import CompaniesPage from './pages/CompaniesPage';
import OffersPage from './pages/OffersPage';
import FavoritesPage from './pages/FavoritesPage';
import OffersPlusPage from './pages/OffersPlusPage';
import Footer from './components/Footer';
import AuditPage from './pages/AuditPage';

import ProfilePage from './pages/Profile/ProfilePage';
import OnboardingFlow from './pages/Onboarding/OnboardingFlow';
import TrouvePlusPage from './pages/TrouvePlusPage';
import FAQPage from './pages/FAQPage';
import { MentionsLegales, CGU, Confidentialite, Contact } from './pages/LegalPages';
import ScrollToTop from './components/ScrollToTop';
import PromoModal from './components/PromoModal';
import FeedbackWidget from './components/FeedbackWidget';
import OnboardingTour from './components/OnboardingTour';
import { AuthProvider } from './context/AuthContext';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import MobileBottomNav from './components/MobileBottomNav';

const queryClient = new QueryClient()

const HOME_JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "name": "Trouve ton VIE",
      "alternateName": "trouvetonvie.fr",
      "url": "https://trouvetonvie.fr",
      "logo": "https://trouvetonvie.fr/logo-512.png",
      "description": "Agrégateur d'offres de Volontariat International en Entreprise (VIE) avec base d'offres mise à jour quotidiennement.",
      "foundingDate": "2025",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer support",
        "email": "contact@trouvetonvie.fr",
        "availableLanguage": ["French"]
      }
    },
    {
      "@type": "WebSite",
      "name": "Trouve ton VIE",
      "url": "https://trouvetonvie.fr",
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": "https://trouvetonvie.fr/offres_vie?search={search_term_string}"
        },
        "query-input": "required name=search_term_string"
      }
    }
  ]
};

const Home = () => (
  <>
    <Helmet>
      <title>Trouve ton VIE — Ne ratez plus aucune offre de VIE</title>
      <meta name="description" content="Agrégateur d'offres VIE : centralisez Business France + sites carrières des entreprises. CV Matching IA, alertes temps réel, 1 000+ offres actives." />
      <link rel="canonical" href="https://trouvetonvie.fr/" />
      <meta property="og:title" content="Trouve ton VIE — Ne ratez plus aucune offre de VIE" />
      <meta property="og:description" content="Agrégateur d'offres VIE : centralisez Business France + sites carrières des entreprises. CV Matching IA, alertes temps réel, 1 000+ offres actives." />
      <meta property="og:url" content="https://trouvetonvie.fr/" />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="https://trouvetonvie.fr/og-image.png" />
      <meta property="og:locale" content="fr_FR" />
      <meta property="og:site_name" content="Trouve ton VIE" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Trouve ton VIE — Ne ratez plus aucune offre de VIE" />
      <meta name="twitter:description" content="Agrégateur d'offres VIE : centralisez Business France + sites carrières des entreprises. CV Matching IA, alertes temps réel, 1 000+ offres actives." />
      <meta name="twitter:image" content="https://trouvetonvie.fr/og-image.png" />
      <script type="application/ld+json">{JSON.stringify(HOME_JSON_LD)}</script>
    </Helmet>
    <HeroSection />
    {/* <ListingsSection /> */}
    {/* Map hidden on mobile */}
    <div className="hidden md:block">
      <MapSection />
    </div>
  </>
);


const OnboardingFlowWrapper = () => {
  const navigate = useNavigate();
  return <OnboardingFlow onComplete={() => navigate('/profil')} />;
};

const MainLayout = () => {
  return (
    <div className="App font-sans min-h-screen bg-[#F0F9FF]">
      <Header />


      <main className="pt-[56px] md:pt-[72px] pb-16 md:pb-0">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/les-entreprises" element={<CompaniesPage />} />
          <Route path="/offres_vie" element={<OffersPage />} />
          <Route path="/offres_vie/:id" element={<OffersPage />} />
          <Route path="/mes-favoris" element={<FavoritesPage />} />
          <Route path="/offres-plus" element={<OffersPlusPage />} />
          <Route path="/profil" element={<ProfilePage />} />
          <Route path="/onboarding" element={<OnboardingFlowWrapper />} />
          <Route path="/trouve-plus" element={<TrouvePlusPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/mentions-legales" element={<MentionsLegales />} />
          <Route path="/cgu" element={<CGU />} />
          <Route path="/confidentialite" element={<Confidentialite />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/audit" element={<AuditPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </Routes>
      </main>
      <Footer />
      <MobileBottomNav />
    </div>
  );
};

const MAINTENANCE_MODE = false; // Set to true to enable maintenance mode

function App() {
  if (MAINTENANCE_MODE) {
    return <MaintenancePage />;
  }

  return (
    <Router>
      <ScrollToTop />
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <MainLayout />
          <PromoModal />
          <FeedbackWidget />
          <OnboardingTour />
          <Analytics />
          <SpeedInsights />
        </AuthProvider>
      </QueryClientProvider>
    </Router>
  )
}

export default App
